// Undo for local actions the server cannot reverse (dismissing a notification, marking a reply
// thread done): show the change now, send it only after the undo window closes. The window is a
// regular undo entry, so the toast's Undo button and `u` both work. If the page goes away first,
// nothing is sent, which is the safe default for acknowledgements.
import { LOCAL_UNDO_MS, registerUndo } from "./undoRegistry";

export interface DeferredOptions {
  message: string;
  /** Show the change locally (optimistic). */
  apply: () => void;
  /** Reverse `apply` when the reader undoes. */
  revert: () => void;
  /** Send the change once the window has passed. */
  commit: () => Promise<unknown> | unknown;
  doneMessage?: string;
  delayMs?: number;
}

export function deferWithUndo({
  message,
  apply,
  revert,
  commit,
  doneMessage = "Undone.",
  delayMs = LOCAL_UNDO_MS,
}: DeferredOptions): void {
  let settled = false;
  apply();
  // The commit waits a moment past the window so an Undo in its last instant still wins.
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    void commit();
  }, delayMs + 150);
  registerUndo({
    message,
    durationMs: delayMs,
    doneMessage,
    undo: () => {
      if (settled) throw new Error("already committed");
      settled = true;
      clearTimeout(timer);
      revert();
    },
  });
}
